import React, { useState } from "react";
import useSWR from "swr";
import api, { money, fmtDateTime } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Truck, AlertTriangle, Pencil, Copy } from "lucide-react";
import { toast } from "sonner";
import { TRANSPORT_STATUS } from "@/lib/labels";

const fetcher = (u) => api.get(u).then((r) => r.data);
const empty = { tour_id: "", company: "", vehicle_type: "Van", plate: "", capacity_pax: 6, capacity_luggage: 6, pickup_place: "", pickup_datetime: "", return_place: "", return_datetime: "", price: 0, currency: "USD", status: "in_progress" };

export default function Transport() {
  const { data: tours } = useSWR("/tours", fetcher);
  const { data: parts } = useSWR("/participants", fetcher);
  const { data: transports, mutate } = useSWR("/transports", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(empty);

  const submit = async () => {
    if (!form.tour_id) return toast.error("Selecciona un tour");
    try {
      await api.post("/transports", { ...form, capacity_pax: Number(form.capacity_pax), capacity_luggage: Number(form.capacity_luggage), price: Number(form.price) });
      toast.success("Transporte añadido");
      setForm(empty); setOpen(false); mutate();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setEditForm({
      tour_id: t.tour_id || "",
      company: t.company || "",
      vehicle_type: t.vehicle_type || "Van",
      plate: t.plate || "",
      capacity_pax: t.capacity_pax ?? 6,
      capacity_luggage: t.capacity_luggage ?? 6,
      pickup_place: t.pickup_place || "",
      pickup_datetime: t.pickup_datetime || "",
      return_place: t.return_place || "",
      return_datetime: t.return_datetime || "",
      price: t.price ?? 0,
      currency: t.currency || "USD",
      status: t.status || "in_progress",
      notes: t.notes || "",
    });
  };

  const saveEdit = async () => {
    if (!editId) return;
    if (!editForm.tour_id) return toast.error("Selecciona un tour");
    try {
      await api.patch(`/transports/${editId}`, {
        ...editForm,
        capacity_pax: Number(editForm.capacity_pax) || 0,
        capacity_luggage: Number(editForm.capacity_luggage) || 0,
        price: Number(editForm.price) || 0,
      });
      toast.success("Transporte actualizado");
      setEditId(null);
      mutate();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar"); }
  };

  const duplicate = async (t) => {
    try {
      const { id: _id, created_at, updated_at, ...rest } = t;
      await api.post("/transports", { ...rest, company: `${t.company} (copia)` });
      toast.success("Transporte duplicado");
      mutate();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo duplicar"); }
  };

  const tourName = (id) => tours?.find((t) => t.id === id)?.name || "—";
  const paxCount = (tid) => (parts || []).filter((p) => p.tour_id === tid && ["deposit_paid","confirmed","partial_paid","fully_paid"].includes(p.booking_status)).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Transporte</h1><p className="text-slate-500 mt-1">Vehículos, capacidad y logística.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-transport-btn" className="bg-[#026FCB] hover:bg-[#0358a5]"><Plus size={16} className="mr-2" />Nuevo transporte</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Nuevo transporte</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Tour</Label>
                <Select value={form.tour_id} onValueChange={(v) => setForm({...form, tour_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent>{(tours || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Empresa</Label><Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} /></div>
              <div><Label>Vehículo</Label><Input value={form.vehicle_type} onChange={(e) => setForm({...form, vehicle_type: e.target.value})} /></div>
              <div><Label>Matrícula</Label><Input value={form.plate} onChange={(e) => setForm({...form, plate: e.target.value})} /></div>
              <div><Label>Capacidad pax</Label><Input type="number" value={form.capacity_pax} onChange={(e) => setForm({...form, capacity_pax: e.target.value})} /></div>
              <div><Label>Recogida (lugar)</Label><Input value={form.pickup_place} onChange={(e) => setForm({...form, pickup_place: e.target.value})} /></div>
              <div><Label>Recogida (fecha)</Label><Input type="datetime-local" value={form.pickup_datetime} onChange={(e) => setForm({...form, pickup_datetime: e.target.value})} /></div>
              <div><Label>Devolución (lugar)</Label><Input value={form.return_place} onChange={(e) => setForm({...form, return_place: e.target.value})} /></div>
              <div><Label>Devolución (fecha)</Label><Input type="datetime-local" value={form.return_datetime} onChange={(e) => setForm({...form, return_datetime: e.target.value})} /></div>
              <div><Label>Precio</Label><Input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} /></div>
              <div><Label>Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["draft","in_progress","confirmed","cancelled"].map((s) => <SelectItem key={s} value={s}>{TRANSPORT_STATUS[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={submit}>Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {(transports || []).map((t) => {
          const px = paxCount(t.tour_id);
          // Sum capacity of ALL transports for the same tour to decide shortage
          const totalCapacity = (transports || [])
            .filter((x) => x.tour_id === t.tour_id)
            .reduce((s, x) => s + (Number(x.capacity_pax) || 0), 0);
          const tourShortage = px > totalCapacity;
          return (
            <Card key={t.id}><CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-lg flex items-center gap-2"><Truck size={18} className="text-[#026FCB]" />{t.company}</div>
                  <div className="text-sm text-slate-500 mt-0.5">{t.vehicle_type} · {t.plate || "—"}</div>
                  <div className="text-xs text-slate-500 mono mt-2">{tourName(t.tour_id)}</div>
                </div>
                <StatusBadge value={t.status} />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-slate-500 mono uppercase">Capacidad</div>
                  <div className="font-medium mt-0.5">
                    {t.capacity_pax} pax
                    {totalCapacity !== t.capacity_pax && (
                      <span className="text-slate-400 ml-1">({totalCapacity} total)</span>
                    )}
                  </div>
                </div>
                <div><div className="text-slate-500 mono uppercase">Pax previstos</div><div className={`font-medium mt-0.5 ${tourShortage ? "text-red-600" : ""}`}>{px}</div></div>
                <div><div className="text-slate-500 mono uppercase">Precio</div><div className="font-medium mt-0.5">{money(t.price, t.currency)}</div></div>
              </div>
              {tourShortage && (
                <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 dark:bg-red-950 dark:text-red-300 rounded-md px-2 py-1.5">
                  <AlertTriangle size={14} /> Capacidad total insuficiente ({totalCapacity} plazas para {px} pax).
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`edit-transport-${t.id}`}
                  onClick={() => openEdit(t)}
                  title="Editar transporte"
                  className="h-8"
                >
                  <Pencil size={13} className="mr-1" />Editar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`duplicate-transport-${t.id}`}
                  onClick={() => duplicate(t)}
                  title="Duplicar transporte"
                  className="h-8"
                >
                  <Copy size={13} className="mr-1" />Duplicar
                </Button>
              </div>
            </CardContent></Card>
          );
        })}
        {(!transports || transports.length === 0) && <Card><CardContent className="p-10 text-center text-slate-500">Sin transporte registrado.</CardContent></Card>}
      </div>

      {/* Editar transporte dialog */}
      <Dialog open={!!editId} onOpenChange={(o) => !o && setEditId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Editar transporte</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Tour</Label>
              <Select value={editForm.tour_id} onValueChange={(v) => setEditForm({...editForm, tour_id: v})}>
                <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>{(tours || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Empresa</Label><Input data-testid="edit-tr-company" value={editForm.company} onChange={(e) => setEditForm({...editForm, company: e.target.value})} /></div>
            <div><Label>Vehículo</Label><Input value={editForm.vehicle_type} onChange={(e) => setEditForm({...editForm, vehicle_type: e.target.value})} /></div>
            <div><Label>Matrícula</Label><Input value={editForm.plate} onChange={(e) => setEditForm({...editForm, plate: e.target.value})} /></div>
            <div><Label>Capacidad pax</Label><Input type="number" value={editForm.capacity_pax} onChange={(e) => setEditForm({...editForm, capacity_pax: e.target.value})} /></div>
            <div><Label>Recogida (lugar)</Label><Input value={editForm.pickup_place} onChange={(e) => setEditForm({...editForm, pickup_place: e.target.value})} /></div>
            <div><Label>Recogida (fecha)</Label><Input type="datetime-local" value={editForm.pickup_datetime} onChange={(e) => setEditForm({...editForm, pickup_datetime: e.target.value})} /></div>
            <div><Label>Devolución (lugar)</Label><Input value={editForm.return_place} onChange={(e) => setEditForm({...editForm, return_place: e.target.value})} /></div>
            <div><Label>Devolución (fecha)</Label><Input type="datetime-local" value={editForm.return_datetime} onChange={(e) => setEditForm({...editForm, return_datetime: e.target.value})} /></div>
            <div><Label>Precio</Label><Input type="number" value={editForm.price} onChange={(e) => setEditForm({...editForm, price: e.target.value})} /></div>
            <div><Label>Estado</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["draft","in_progress","confirmed","cancelled"].map((s) => <SelectItem key={s} value={s}>{TRANSPORT_STATUS[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditId(null)}>Cancelar</Button>
            <Button data-testid="edit-tr-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={saveEdit}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
